/** @format */

import {
	ISkuLocation,
	ProductListSEO,
	ProductPrice,
	ProductSku
} from '@shared/data-access/interfaces';
import { ProductSelectionCoreState, ProductSelectionState } from '@pdp/data-access/interfaces';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { BrandLinks, TimeService } from '@pdp/utils';
import { UtilityService } from '@shared/utils/services';
import { formatDate } from '@angular/common';
import { selectNearbyStores } from './header-location/header-location.selectors';

const timeService = new TimeService();

/**  @description Product State Selectors for Product Details API */
export const selectProductState = createFeatureSelector<ProductSelectionState>('productSelection');
export const selectProductSelectionCore = createSelector(
	selectProductState,
	(state) => state.productSelectionCore
);

export const selectAllProductStyle = createSelector(
	selectProductSelectionCore,
	(state) => state.productStyle
);

export const selectProductPartNumber = createSelector(
	selectAllProductStyle,
	(state) => state?.partNumber
);

export const selectBundleData = createSelector(
	selectProductSelectionCore,
	(state) => state.bundleData
);

export const selectBundleProductsData = createSelector(
	selectProductSelectionCore,
	(state) => state.bundleProductsData
);

export const selectBundleOverallPrice = createSelector(selectBundleData, (state) => {
	const prices = state.prices;

	/**
	 * If the bundle has no prices (i.e. its out-of-stock), if we return a falsy value, it'll be treated as OOS
	 * downstream in the Price component.
	 */
	if (!prices) {
		return null;
	}

	const displayedPrice: ProductPrice = {
		minOfferPrice: Number(prices.minofferprice),
		minListPrice: Number(prices.minlistprice),
		maxOfferPrice: Number(prices.maxofferprice),
		maxListPrice: Number(prices.maxlistprice),
		minSavingPct: '',
		maxSavingPct: '',
		emptyPrice: false
	};

	const minDiscount = Math.floor(
		((Number(prices.minlistprice) - Number(prices.minofferprice)) /
			Number(prices.minlistprice)) *
			100
	);
	const maxDiscount = Math.floor(
		((Number(prices.maxlistprice) - Number(prices.maxofferprice)) /
			Number(prices.maxlistprice)) *
			100
	);

	if (minDiscount >= 5 || maxDiscount >= 5) {
		if (minDiscount < maxDiscount) {
			displayedPrice.minSavingPct = minDiscount;
			displayedPrice.maxSavingPct = maxDiscount;
		} else if (minDiscount > maxDiscount && maxDiscount >= 5) {
			displayedPrice.minSavingPct = maxDiscount;
			displayedPrice.maxSavingPct = minDiscount;
		} else {
			displayedPrice.minSavingPct = minDiscount;
		}
	}

	return displayedPrice;
});

export const selectBundleProductsDataPartNumbers = createSelector(
	selectBundleProductsData,
	(state) => {
		const bundleSkus = state.map((product) => {
			if (!product.skus) {
				return [];
			}

			return product.skus.map((sku) => sku.partNumber);
		});

		return bundleSkus.flat();
	}
);

export const selectAllProductSkus = createSelector(
	selectProductSelectionCore,
	(state) => state.productSkus
);

export const selectSelectedPersonalizationOptions = createSelector(
	selectProductSelectionCore,
	(state) => state.selectedPersonalizationOptions
);

export const selectIsPersonalizedProduct = createSelector(
	selectAllProductStyle,
	(state) => state.isPersonalizable
);

export const selectAllProductSkusPartNumber = createSelector(selectAllProductSkus, (state) =>
	state?.map((sku) => sku.partNumber)
);

export const selectEcommInventory = createSelector(
	selectProductSelectionCore,
	(state) => state.ecommInventory
);

export const selectBopisInventory = createSelector(
	selectProductSelectionCore,
	(state) => state.bopisInventory
);

export const selectResolvedSku = createSelector(
	selectProductSelectionCore,
	(state) => state.resolvedSku
);

export const selectResolvedSkuPartNumber = createSelector(
	selectResolvedSku,
	(state) => state?.partNumber
);

export const selectResolvedSkuInventory = createSelector(
	selectProductSelectionCore,
	(state) => state.resolvedSkuInventory
);

export const selectIsResolvedSkuStorePickupAvailable = createSelector(
	selectBopisInventory,
	selectResolvedSkuPartNumber,
	(storeInventory, resolvedSkuPartNumber): boolean => {
		if (!storeInventory || !resolvedSkuPartNumber) {
			return false;
		}

		const resolvedSkuInventory = storeInventory.find(
			(inventoryDataForSku) => inventoryDataForSku.sku === resolvedSkuPartNumber
		);
		if (!resolvedSkuInventory) {
			return false;
		}

		return Number(resolvedSkuInventory.atsqty) > 0 || Number(resolvedSkuInventory.boplqty) > 0;
	}
);

export const selectResolvedSkuSDDInventory = createSelector(
	selectResolvedSkuInventory,
	(state) => state?.sdd
);

export const selectNearbyAvailableStoreCount = createSelector(
	selectNearbyStores,
	selectResolvedSku,
	(nearbyStores, resolvedSku) => {
		if (resolvedSku && nearbyStores) {
			const sku = resolvedSku.partNumber;
			return nearbyStores[sku].length;
		}

		return 0;
	}
);

export const selectEDDdata = createSelector(selectProductSelectionCore, (state) => {
	if (state.eddInfo) {
		const skuData = state.eddInfo.data.bySku[0].byServiceLevel;

		// Check if response contains serviceLevel P_Ground
		return skuData.find((eddData) => eddData.serviceLevel.includes('P_Ground'));
	}

	return null;
});

export const selectEDDDisplayVerbiage = createSelector(selectEDDdata, (skuEDDdata) => {
	let skuVerbiageEDD;

	if (skuEDDdata === null) {
		return null;
	} else if (skuEDDdata) {
		skuVerbiageEDD = `Est: ${formatDate(
			skuEDDdata.promiseStartEddDate,
			'E L/d',
			'en-US'
		)}-${formatDate(skuEDDdata.promiseEndEddDate, 'E L/d', 'en-US')} `;
		return skuVerbiageEDD;
	} else {
		return 'na';
	}
});

export const selectEDDDisplayText = createSelector(selectEDDdata, (skuEDDdata) => {
	let shipMessageEDD;

	if (skuEDDdata === null) {
		return '';
	} else if (skuEDDdata) {
		if (skuEDDdata.minEddDays && skuEDDdata.minEddDays > 0 && skuEDDdata.minEddDays <= 11) {
			shipMessageEDD = `${skuEDDdata.minEddDays}-${skuEDDdata.maxEddDays} days`;
		} else if (skuEDDdata.minEddDays % 5 === 0 && skuEDDdata.minEddDays > 11) {
			shipMessageEDD = `${skuEDDdata.minEddDays / 5} weeks`;
		} else if (skuEDDdata.minEddDays % 5 !== 0 && skuEDDdata.minEddDays > 11) {
			// Calculate number of weeks (Using bitwise operator to round)
			// eslint-disable-next-line no-bitwise
			shipMessageEDD = `${(skuEDDdata.minEddDays / 5) >> 0} - ${
				// eslint-disable-next-line no-bitwise
				(skuEDDdata.minEddDays / 5 + 1) >> 0
			} weeks`;
		} else {
			// Hide the shipping message
			shipMessageEDD = 'na';
		}
	} else {
		shipMessageEDD = 'na';
	}

	return shipMessageEDD;
});

export const selectPrimaryCategory = createSelector(
	selectProductSelectionCore,
	(state) => state.primaryCategory
);

export const selectBreadcrumbDetails = createSelector(
	selectProductSelectionCore,
	(state) => (state?.primaryCategory as ProductListSEO)?.breadCrumbDetails
);

export const selectHasEmptyPriceAndHasPrice = createSelector(
	selectProductSelectionCore,
	(state) => state.hasEmptyPriceAndHasPrice
);

export const selectShippingInfo = createSelector(
	selectProductSelectionCore,
	(state: ProductSelectionCoreState) => state.shippingInfo
);

export const selectShippingData = createSelector(
	selectShippingInfo,
	selectResolvedSku,
	(shipping, resolvedSku) => {
		if (resolvedSku != undefined && !shipping.comingSoon) {
			return shipping;
		}

		return null;
	}
);

export const selectTextileLabelInfo = createSelector(selectResolvedSku, (sku) => {
	if (!sku) {
		return null;
	}

	const textileAttr = sku.descriptiveAttributes.find((attr) => attr.identifier === '6077');
	return textileAttr ? textileAttr.value : null;
});

export const selectPreSaleInfo = createSelector(
	selectShippingInfo,
	selectResolvedSku,
	(shippingInfo, resolvedSku: ProductSku) => {
		let isPreSale: boolean;
		let preSaleDate: string;

		if (resolvedSku) {
			isPreSale = resolvedSku.preSale;
			preSaleDate = resolvedSku.preSaleEndDate;
		} else {
			isPreSale = shippingInfo.isPreSale;
			preSaleDate = shippingInfo.preSaleDate;
		}

		const isPreSaleCurrent =
			isPreSale &&
			!!preSaleDate &&
			UtilityService.isDateAfterNowDate(preSaleDate, 'MM-dd-yyyy');

		return {
			isPreSale,
			preSaleDate,
			isPreSaleCurrent
		};
	}
);

export const selectIsPreSale = createSelector(
	selectPreSaleInfo,
	(preSaleInfo) => preSaleInfo.isPreSaleCurrent
);

export const selectIsComingSoon = createSelector(
	selectResolvedSku,
	selectAllProductSkus,
	(resolvedSku, productSkus) => {
		let rawComingSoonDate;
		if (resolvedSku?.comingSoonEndDate) {
			rawComingSoonDate = resolvedSku.comingSoonEndDate;
		} else {
			for (const sku of productSkus) {
				if (sku.isEMComingSoon && sku.comingSoonEndDate) {
					rawComingSoonDate = sku.comingSoonEndDate;
				} else {
					rawComingSoonDate = '';
					break;
				}
			}
		}
		if (rawComingSoonDate) {
			const currentDate = timeService.getCurrentTime().toJSDate();
			const comingSoonDate = timeService
				.parseDateFromFormat(rawComingSoonDate, 'M/d/yyyy h:mm:ss a')
				.toJSDate();
			return Boolean(comingSoonDate > currentDate);
		} else {
			return false;
		}
	}
);

export const selectBopisAndEcommInventory = createSelector(
	selectEcommInventory,
	selectBopisInventory,
	(ecommInventory, bopisInventory) => ({ ecommInventory, bopisInventory })
);

export const selectPromotionData = createSelector(
	selectProductSelectionCore,
	(state) => state.promotionData
);

export const selectcustomProductData = createSelector(
	selectProductSelectionCore,
	(state) => state.customProductData
);

export const selectCustomProductPrice = createSelector(
	selectcustomProductData,
	(customProductData) => customProductData?.totalPrice
);

/**
 * @description return bopis inventory object for a
 * resolved sku and specific location ( aka the user store set )
 * Use this instead of looping through the resolvedSkuInventory object in state
 */
export const selectResolvedSkuBopisInventory = createSelector(
	selectProductSelectionCore,
	selectBopisInventory,
	(state, bopisInventory) => {
		if (state.resolvedSku && bopisInventory) {
			return bopisInventory.find(
				(skuInventoryObject) => skuInventoryObject.sku === state.resolvedSku.partNumber
			);
		}
	}
);

export const selectResolvedSkuEcommInventory = createSelector(
	selectResolvedSkuPartNumber,
	selectEcommInventory,
	(resolvedSkuPartNumber: string, bopisInventory: ISkuLocation[]) => {
		if (resolvedSkuPartNumber && bopisInventory) {
			return bopisInventory.find(
				(skuInventoryObject) => skuInventoryObject.sku === resolvedSkuPartNumber
			);
		}
	}
);

export const selectShouldShowAlert = createSelector(selectResolvedSkuInventory, (state) => {
	const atsQty = parseInt(state?.ecomm?.atsqty, 10);
	const isaQty = parseInt(state?.ecomm?.isaqty, 10);

	return Boolean(atsQty < 10 || isaQty < 10);
});

export const selectLoadingProductImages = createSelector(
	selectProductSelectionCore,
	(state): boolean => state.loadingProductImages
);

export const selectFBTProducts = createSelector(
	selectProductSelectionCore,
	(state) => state.FBTProducts
);

export const selectDefiningAttributes = createSelector(
	selectAllProductStyle,
	(product) => product.definingAttributes
);

export const selectDefiningAttributesLength = createSelector(
	selectDefiningAttributes,
	(product): number => product?.length
);

export const selectDescriptiveAttributes = createSelector(
	selectAllProductStyle,
	(product) => product.descriptiveAttributes
);

export const selectSustainabilityAttributes = createSelector(
	selectDescriptiveAttributes,
	(product) => {
		const sustainAttr = [];

		product.forEach((attribute) => {
			if (attribute.identifier === '5503') {
				sustainAttr.push(attribute.value);
			}
		});

		return sustainAttr;
	}
);

export const selectEcodeLevelPrice = createSelector(
	selectAllProductStyle,
	(state): ProductPrice => ({
		maxListPrice: parseFloat(state.prices.maxlistprice),
		maxOfferPrice: parseFloat(state.prices.maxofferprice),
		minListPrice: parseFloat(state.prices.minlistprice),
		minOfferPrice: parseFloat(state.prices.minofferprice),
		mapPriceIndicator: state.prices.mapPriceIndicator,
		priceIndicator: state.prices.priceIndicator,
		ecodeLevelPrice: true
	})
);

export const selectSkuToEcommInventoryMap = createSelector(
	selectEcommInventory,
	(ecommInventory) => {
		if (!ecommInventory) {
			return null;
		}

		const ecommInventoryMap = {};
		ecommInventory.forEach((inventoryData) => {
			ecommInventoryMap[inventoryData.sku] = inventoryData;
		});
		return ecommInventoryMap;
	}
);

export const selectSkuToBopisInventoryMap = createSelector(
	selectBopisInventory,
	(bopisInventory) => {
		if (!bopisInventory) {
			return null;
		}

		const bopisInventoryMap = {};
		bopisInventory.forEach((inventoryData) => {
			bopisInventoryMap[inventoryData.sku] = inventoryData;
		});
		return bopisInventoryMap;
	}
);

export const selectSkusWithInventory = createSelector(
	selectAllProductSkus,
	selectSkuToEcommInventoryMap,
	selectSkuToBopisInventoryMap,
	selectNearbyStores,
	(skus, ecommInventory, bopisInventory, nearbyStores) => {
		if (!ecommInventory || !bopisInventory || !nearbyStores) {
			return {
				skus,
				isDataInitialized: false
			};
		}

		const filteredSkus = skus.filter((sku) => {
			const isEcommAvailable =
				ecommInventory[sku.partNumber] && Number(ecommInventory[sku.partNumber].atsqty) > 0;
			const isBopisAvailable =
				bopisInventory[sku.partNumber] &&
				(Number(bopisInventory[sku.partNumber].atsqty) > 0 ||
					Number(bopisInventory[sku.partNumber].isaqty) > 0 ||
					Number(bopisInventory[sku.partNumber].boplqty) > 0);
			return isEcommAvailable || isBopisAvailable || nearbyStores[sku.partNumber].length > 0;
		});

		return {
			skus: filteredSkus,
			isDataInitialized: true
		};
	}
);

export const selectBrandLink = createSelector(selectAllProductStyle, (style) => {
	const brand = style.descriptiveAttributes?.find((attr) => attr.identifier === 'X_BRAND')
		?.value as string;

	if (!brand || typeof brand !== 'string') {
		return;
	}

	const url = BrandLinks[brand.toLowerCase()];

	if (!url) {
		return;
	}

	return {
		name: brand,
		url
	};
});

export const selectIsBOPISLOnly = createSelector(
	selectAllProductStyle,
	(productStyle) =>
		!!productStyle.descriptiveAttributes?.some(
			(attribute) => attribute.identifier === '6105' && attribute.value === 'BOPISL Only'
		)
);
